// =====================================================================
// Aba — Unidades (agenda por data; Instituto Melo / médicos viajantes).
// CRUD de clinic_units via endpoints v2 do n8n (/v2/admin/units).
// Só aparece pra tenants com clinic_config.enable_date_agenda = true.
// Define a praça (cidade) + horário/vagas PADRÃO; a "Agenda por data"
// herda esses padrões por dia (ou sobrescreve num dia específico).
// =====================================================================
import React from "react";
import { TextField } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import CrudTable from "../components/CrudTable";
import CrudDialog from "../components/CrudDialog";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import useCrudResource from "../hooks/useCrudResource";

import {
  listUnits,
  createUnit,
  updateUnit,
  deleteUnit
} from "../../../services/clinicAdminService";

const trimTime = (t) => (t ? String(t).slice(0, 5) : "");

// input de hora reaproveitado nos dois campos de horário padrão
const timeField = (value, setValue) => (
  <TextField
    type="time"
    variant="outlined"
    size="small"
    fullWidth
    value={trimTime(value)}
    onChange={(e) => setValue(e.target.value)}
    InputLabelProps={{ shrink: true }}
    inputProps={{ step: 300 }}
  />
);

const TIMEZONES = [
  { value: "America/Sao_Paulo", label: "São Paulo (Sudeste/Sul/Centro-Oeste)" },
  { value: "America/Recife", label: "Recife (Nordeste)" },
  { value: "America/Bahia", label: "Bahia" },
  { value: "America/Manaus", label: "Manaus (AM/RR/RO/MT)" },
  { value: "America/Belem", label: "Belém (PA/AP)" }
];

const COLUMNS = [
  { key: "name", label: "Unidade" },
  { key: "city", label: "Cidade", width: 150, render: (r) => r.city || "—" },
  {
    key: "horario",
    label: "Horário padrão",
    width: 150,
    render: (r) =>
      r.default_start_time && r.default_end_time
        ? `${trimTime(r.default_start_time)}–${trimTime(r.default_end_time)}`
        : "—"
  },
  {
    key: "default_capacity",
    label: "Vagas/dia",
    width: 90,
    align: "right",
    render: (r) => r.default_capacity ?? "—"
  },
  {
    key: "is_active",
    label: "Ativa",
    width: 70,
    align: "center",
    render: (r) =>
      r.is_active ? (
        <CheckCircleIcon fontSize="small" htmlColor="#06c270" />
      ) : (
        "—"
      )
  }
];

const FIELDS = [
  {
    key: "unit_key",
    label: "Chave (slug)",
    type: "text",
    required: true,
    colSpan: 6,
    regex: /^[a-z0-9_-]+$/i,
    regexError: "Apenas letras, números, _ e -",
    placeholder: "moema",
    helperText: "Identificador único. Imutável após criar."
  },
  {
    key: "name",
    label: "Nome da unidade",
    type: "text",
    required: true,
    colSpan: 6,
    placeholder: "Moema — São Paulo"
  },
  { key: "city", label: "Cidade", type: "text", colSpan: 6, placeholder: "São Paulo" },
  { key: "state", label: "UF", type: "text", colSpan: 6, placeholder: "SP" },
  {
    key: "timezone",
    label: "Fuso horário",
    type: "select",
    colSpan: 6,
    options: TIMEZONES,
    helperText: "Padrão: São Paulo"
  },
  {
    key: "whatsapp_connection_id",
    label: "Conexão WhatsApp (id)",
    type: "number",
    colSpan: 6,
    helperText: "Número oficial da praça (Fase 3) — opcional",
    transform: (v) => (v === "" ? null : Number(v))
  },
  {
    key: "default_start_time",
    label: "Abre às (padrão)",
    type: "custom",
    colSpan: 4,
    render: (v, set) => timeField(v, set)
  },
  {
    key: "default_end_time",
    label: "Fecha às (padrão)",
    type: "custom",
    colSpan: 4,
    render: (v, set) => timeField(v, set)
  },
  {
    key: "default_capacity",
    label: "Vagas/dia (padrão)",
    type: "number",
    colSpan: 4,
    transform: (v) => (v === "" ? null : Number(v))
  },
  {
    key: "default_slot_minutes",
    label: "Intervalo dos horários (min)",
    type: "number",
    colSpan: 6,
    placeholder: "30",
    helperText: "De quanto em quanto tempo aparece um horário. Padrão 30.",
    transform: (v) => (v === "" ? null : Number(v))
  },
  {
    key: "sort_order",
    label: "Ordem",
    type: "number",
    colSpan: 6,
    transform: (v) => (v === "" ? null : Number(v))
  },
  { key: "is_active", label: "Ativa", type: "switch", colSpan: 6 }
];

const UnidadesTab = ({ slug }) => {
  const crud = useCrudResource({
    slug,
    list: listUnits,
    create: createUnit,
    update: updateUnit,
    remove: deleteUnit,
    resourceLabel: "unidade"
  });

  return (
    <>
      <CrudTable
        columns={COLUMNS}
        rows={crud.rows}
        loading={crud.loading}
        onAdd={crud.handleAdd}
        onEdit={crud.handleEdit}
        onDelete={crud.handleDelete}
        searchPlaceholder="Buscar unidade…"
        searchFields={["name", "city", "unit_key"]}
      />

      <CrudDialog
        open={crud.dialogOpen}
        onClose={crud.handleCloseDialog}
        onSave={crud.handleSave}
        title={crud.dialogInitial ? "Editar unidade" : "Nova unidade"}
        fields={FIELDS}
        initialValues={crud.dialogInitial}
        saving={crud.saving}
      />

      <ConfirmDeleteDialog
        open={!!crud.deleteTarget}
        onClose={crud.handleCancelDelete}
        onConfirm={crud.handleConfirmDelete}
        itemLabel={crud.deleteTarget?.name}
        saving={crud.saving}
      />
    </>
  );
};

export default UnidadesTab;
